import React, { useEffect, useState } from 'react';
import { RecycleBin } from '@sharefiledev/icons';
import { Button, Col, Flex, Form, Input, Row, Select, theme } from 'antd';
import { useGetScreenSize } from '../../../common/hooks/useGetScreenSize';
import { useAppStore } from '../../../common/store';
import { ICON_SIZE, MAX_NAME_LENGTH } from '../../../constants';
import {
	DataSourceItem,
	Entity,
	Variable,
} from '../../../data/interfaces/DocumentTemplate';
import { InputValidation } from '../../../data/interfaces/form';
import { fetchEntitiesByDataSource } from '../../../data/variablesDataApi';
import { t } from '../../../utils';

type VariableItemProps = {
	id: number;
	variable: Variable;
	onRemove: (id: string) => void;
	onUpdate: (obj: Partial<Variable>, id: string) => void;
	dataSources: DataSourceItem[];
	validationState: InputValidation;
	isShownInModal: boolean;
};

export const VariableItem = ({
	id,
	variable,
	onRemove,
	onUpdate,
	dataSources,
	validationState,
	isShownInModal,
}: VariableItemProps) => {
	const { isExtraSmallScreen } = useGetScreenSize();
	const { token } = theme.useToken();
	const [selectedDataSource, setSelectedDataSource] = useState(variable?.connectionRID);
	const [selectedEntity, setSelectedEntity] = useState(variable?.entityId);
	const [entities, setEntities] = useState([]);

	const { documentTemplate } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
	}));

	const isDisabled = () =>
		variable.provider &&
		variable.name &&
		documentTemplate?.fields?.some(field =>
			field.name.includes(`${variable.provider}.${variable.name}`)
		);

	async function onDataSourceChange(connectionRID) {
		setSelectedDataSource(connectionRID);
		onUpdate(
			{
				connectionRID,
			},
			variable.id
		);
		setSelectedEntity(null);
		setEntities([]);

		if (connectionRID) {
			const response = await fetchEntitiesByDataSource(connectionRID);
			const mappedEntities: Entity[] = response.map((d: Entity) => {
				const dataSourceOption: any = { ...d };

				dataSourceOption.label = d.entity;
				dataSourceOption.value = d.RID;
				return dataSourceOption;
			});
			setEntities(mappedEntities);
		}
	}

	function onSourceRecordChange(value) {
		setSelectedEntity(value);

		const schema: Entity = entities.find(d => d.RID === value);
		onUpdate(
			{
				entity: schema.entity,
				entityId: value,
				provider: schema.provider,
				version: schema.version,
			},
			variable.id
		);
	}

	function onVariableNameChange(value) {
		onUpdate({ name: value }, variable.id);
	}

	function getLabelWithIndex(label: string, index: number) {
		return `${label} ${index}`;
	}

	useEffect(() => {
		const onMount = async () => {
			const connectionRID = variable?.connectionRID;
			if (connectionRID) {
				const response = await fetchEntitiesByDataSource(connectionRID);
				const mappedEntities: Entity[] = response.map(d => {
					const dataSourceOption: any = { ...d };
					dataSourceOption.label = d.entity;
					dataSourceOption.value = d.RID;
					return dataSourceOption;
				});
				setEntities(mappedEntities);
			}
		};

		onMount();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fieldStyle = { width: isExtraSmallScreen ? 150 : 300 };

	return (
		<Flex
			vertical
			style={{
				marginBlockEnd: token.marginXS,
			}}
		>
			<Row
				gutter={isShownInModal ? 0 : { xs: 8, sm: 16, md: 24, lg: 32 }}
				justify="center"
				align="top"
			>
				<Col span={22}>
					<Row
						gutter={isShownInModal ? 0 : { xs: 8, sm: 16, md: 24, lg: 32 }}
						align="middle"
					>
						<Col xs={24} sm={11} md={11} lg={11}>
							<Form.Item
								label={getLabelWithIndex(t('variables.dataSource'), id)}
								required
								aria-label={getLabelWithIndex(t('variables.dataSource'), id)}
							>
								<Select
									onChange={e => onDataSourceChange(e)}
									value={selectedDataSource}
									style={fieldStyle}
									options={dataSources}
									disabled={isDisabled()}
									aria-label={selectedDataSource}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={11} md={11} lg={11}>
							<Form.Item
								label={getLabelWithIndex(t('variables.sourceRecord'), id)}
								required
								aria-label={getLabelWithIndex(t('variables.sourceRecord'), id)}
							>
								<Select
									onChange={e => onSourceRecordChange(e)}
									value={selectedEntity}
									style={fieldStyle}
									options={entities}
									disabled={!selectedDataSource || isDisabled()}
									aria-label={selectedEntity}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row
						gutter={isShownInModal ? 0 : { xs: 8, sm: 16, md: 24, lg: 32 }}
						align="middle"
					>
						<Col xs={24} sm={11} md={11} lg={11}>
							<Form.Item
								label={getLabelWithIndex(t('variables.variableName'), id)}
								required
								validateStatus={validationState.status}
								help={validationState.message}
								aria-label={getLabelWithIndex(t('variables.variableName'), id)}
							>
								<Input
									onChange={e => onVariableNameChange(e.target.value)}
									value={variable.name}
									maxLength={MAX_NAME_LENGTH}
									style={fieldStyle}
									aria-label={variable.name}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Col>
				<Col span={2}>
					<Button
						type="text"
						icon={<RecycleBin size={ICON_SIZE} />}
						onClick={() => onRemove(variable.id)}
						aria-label={t('variables.deleteButtonAriaLabel', {
							variableName: variable.name ? variable.name : id,
						})}
					/>
				</Col>
			</Row>
		</Flex>
	);
};
